/**
 * WordPress dependencies
 */
import domReady from '@safe-wordpress/dom-ready';
import { StrictMode, render } from '@safe-wordpress/element';

/**
 * External dependencies
 */
import { registerCommands } from '@nab/commands';
import { registerCoreExperiments } from '@nab/experiment-library';
import { registerCoreSegmentationRules } from '@nab/segmentation-rule-library';
import {
	isExperimentTooOldToHaveHeatmaps,
	createStagingNotice,
} from '@nab/utils';
import type { ExperimentId, ExperimentTypeName, SiteId } from '@nab/types';

/**
 * Internal dependencies
 */
import { ResultsNotAvailable } from './components/results-not-available';
import { ResultsProvider } from './components/provider';
import { Layout } from './components/layout';

type Settings = {
	readonly alternativeIndex: number;
	readonly endDate: string | false;
	readonly experimentId: ExperimentId;
	readonly experimentType: ExperimentTypeName;
	readonly firstDayOfWeek: number;
	readonly staging: 'environment-type' | 'url' | false;
	readonly isPublicView: boolean;
	readonly isReadOnlyActive: boolean;
	readonly siteId: SiteId;
};

export function initPage( id: string, settings: Settings ): void {
	const content = document.getElementById( id );

	if ( isExperimentTooOldToHaveHeatmaps( settings.endDate || '' ) ) {
		return render(
			<StrictMode>
				<ResultsNotAvailable />
			</StrictMode>,
			content
		);
	}

	const {
		experimentId,
		experimentType,
		alternativeIndex,
		isPublicView,
		isReadOnlyActive,
		staging,
		firstDayOfWeek,
	} = settings;

	createStagingNotice( staging );
	registerCoreExperiments();
	registerCoreSegmentationRules();

	domReady( () => registerCommands() );

	render(
		<StrictMode>
			<ResultsProvider
				experimentId={ experimentId }
				experimentType={ experimentType }
				alternativeIndex={ alternativeIndex }
				firstDayOfWeek={ firstDayOfWeek }
				isPublicView={ isPublicView }
				isReadOnlyActive={ isReadOnlyActive }
			>
				<Layout />
			</ResultsProvider>
		</StrictMode>,
		content
	);
}
